/**
 * InitializeSecureStorageUseCase
 * 
 * Initializes SecureStorageWebCrypto with the user's Firebase ID.
 * This is the first step before any storage operations (read/write).
 * 
 * Single Responsibility: Only initialize storage encryption.
 * Does NOT handle sync or user storage - those are separate concerns.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class InitializeSecureStorageUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Initialize SecureStorage with user's Firebase ID
   * 
   * @param firebaseUserId - The Firebase user ID (used for encryption key derivation)
   * @throws Error if initialization fails (critical error)
   */
  async execute(firebaseUserId: string): Promise<void> {
    // console.log('[InitializeSecureStorage] Initializing for user:', firebaseUserId);

    try {
      await this.storage.initialize(firebaseUserId);
      // console.log('[InitializeSecureStorage] Initialization complete');
    } catch (error) {
      appLogger.error('Failed to initialize', 'InitializeSecureStorageUseCase.execute', { error: error as Error });
      throw error; // Re-throw - this is a critical error
    }
  }
}

// Export singleton instance
export const initializeSecureStorageUseCase = new InitializeSecureStorageUseCase();
